// ============================================================
// TERROR — COSMETIC OVERLAY
// Renders equipped cosmetics as layered sprites over the base player.
// Mirrors CompanionSprite's pattern: one component, handles fallback
// gracefully, no-op when nothing is equipped.
//
// Z-ordering (back → front, painter's algorithm):
//   1. back   (capes, wings, banners — behind the player)
//   2. body   (outfits — on top of the base sprite silhouette)
//   3. head   (hats — on top of the head area)
//   4. face   (masks, paint — on top of head)
//   5. aura   (particle effects — on top of everything else)
//
// Title is NOT rendered here. It's text, and lives in the name-tag
// area — renderers that want it use resolveEquipped().title directly.
//
// Props:
//   equipped   { head, face, body, back, aura, title } | null
//   size       px — the player sprite's rendered size
//   gender     "f" | "m" (reserved for future gendered sprite picks)
//   facing     { x, y } (reserved for directional cosmetic variants)
//
// If equipped is null/missing, renders nothing. Safe to drop in
// anywhere — zero cost when the player has no cosmetics.
// ============================================================

import { Sprite } from "./Sprite";
import { resolveEquipped } from "./CosmeticManifest";

// Slot layering order — painter's algorithm, first drawn = behind.
// `back` sits behind the player sprite itself (negative z would be
// ideal but PlayerEntity already manages zIndex; we accept that back
// pieces render on top of the base and adjust opacity/positioning in
// the asset itself. Safer than fighting z-index across zones.)
const SLOT_ORDER = ["back", "body", "head", "face", "aura"];

export function CosmeticOverlay({
  equipped,
  size,
  gender = "f",        // eslint-disable-line no-unused-vars
  facing = { x: 0, y: 1 }, // eslint-disable-line no-unused-vars
}) {
  if (!equipped) return null;

  const resolved = resolveEquipped(equipped);

  return (
    <>
      {SLOT_ORDER.map((slot) => {
        const asset = resolved[slot];
        if (!asset) return null;

        // Each slot is a full-size absolutely-positioned layer, co-registered
        // with the base player sprite. Since the parent <PlayerEntity>
        // wrapper is already sized and positioned, we fill it.
        return (
          <div
            key={slot}
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              // z-index layering within the player's own stacking context.
              // Parent assigns zIndex 10 to the player wrapper, so these
              // siblings stack relative to the base <img> inside it.
              zIndex: SLOT_ORDER.indexOf(slot) + 1,
            }}
          >
            <Sprite
              src={asset}
              size={size}
              fallbackColor="transparent"
              alt={`cosmetic_${slot}`}
            />
          </div>
        );
      })}
    </>
  );
}

// ── TITLE HELPER ──────────────────────────────────────────────
// Tiny helper for name-tag rendering: given an equipped map, return
// the display text of the equipped title (or null).
//
// Usage in a name tag:
//   const title = getEquippedTitle(equipped);
//   return <div>{player.name}{title && <span> · {title}</span>}</div>;
export function getEquippedTitle(equipped) {
  if (!equipped?.title) return null;
  return resolveEquipped(equipped).title || null;
}
